import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Crown, CalendarDays } from "lucide-react";

const Champion = ({ r, i }) => (
  <div className={`flex items-center gap-4 p-4 border ${i === 0 ? "border-red-600 bg-red-50/60" : "border-slate-200 bg-white"}`} data-testid={`hof-rank-${i}`}>
    <span className={`h-9 w-9 shrink-0 rounded-sm flex items-center justify-center font-mono text-sm ${i === 0 ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`}>
      {i === 0 ? <Crown className="h-4 w-4" strokeWidth={2} /> : i + 1}
    </span>
    <div className="flex-1 min-w-0">
      <div className="font-semibold truncate" data-testid={`hof-rank-name-${i}`}>{r.name}</div>
      <div className="text-[11px] text-slate-500 truncate">{r.months.join(" \u00b7 ")}</div>
    </div>
    <div className="text-right">
      <div className="flex items-center gap-1 justify-end text-red-600 font-display text-2xl font-black" data-testid={`hof-trophies-${i}`}>
        {r.trophies} <Trophy className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="text-[10px] font-mono text-slate-500">{r.solved} solved</div>
    </div>
  </div>
);

const MonthRow = ({ e }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`hof-month-${e.period}`}>
    <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" strokeWidth={1.75} />
    <span className="w-32 shrink-0 font-mono text-xs text-slate-600">{e.label}</span>
    {e.winner ? (
      <>
        <Trophy className="h-4 w-4 text-red-600 shrink-0" strokeWidth={2} />
        <span className="flex-1 min-w-0 truncate text-sm font-medium">{e.winner.name}</span>
        <span className="font-mono text-xs text-slate-500 shrink-0">{e.winner.solved} solved</span>
      </>
    ) : (
      <span className="flex-1 text-xs text-slate-400">No winner</span>
    )}
  </div>
);

export default function HallOfFame() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/hall-of-fame").then(({ data: d }) => setData(d)).catch(() => setData({ entries: [], ranking: [] }));
  }, []);
  return (
    <div className="space-y-8" data-testid="hall-of-fame-page">
      <div className="relative overflow-hidden bg-slate-950 text-white border-b-4 border-red-600 p-8 md:p-10">
        <Trophy className="absolute -right-10 -bottom-10 h-56 w-56 text-red-600/15" strokeWidth={1} />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400">Celebrating the team</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3 text-white">Hall of Fame</h1>
        <p className="text-slate-300 mt-3 max-w-lg text-sm">Every Agent of the Month - the top solver of each month - and how many trophies each person has earned.</p>
      </div>
      {!data && <div className="text-xs text-slate-500 font-mono">Loading...</div>}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Trophy count</div>
            {data.ranking.length === 0 && <div className="border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="hof-empty">No trophies awarded yet. The first Agent of the Month appears here after the month ends.</div>}
            {data.ranking.map((r, i) => <Champion key={r.user_id} r={r} i={i} />)}
          </div>
          <div className="lg:col-span-2 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Month by month</div>
            <div className="border border-slate-200 bg-white" data-testid="hof-months">
              {data.entries.length === 0 && <div className="p-6 text-sm text-slate-500">No completed months yet.</div>}
              {data.entries.map((e) => <MonthRow key={e.period} e={e} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
